import { MAX_MESSAGES_THRESHOLD, MESSAGES_THRESHOLD } from "../constants";

const VIRTUALIZED_MAX_MESSAGES = 800;
const VIRTUALIZED_KEPT_MESSAGES = 700;

interface VirtualizedChatRoom {
  /** The viewer is at (or near) the newest loaded messages. */
  isViewingLatest: boolean;
  mounts: number;
}

const virtualizedChatRooms = new Map<string, VirtualizedChatRoom>();

export const registerVirtualizedChatRoom = (uuid: string) => {
  const room = virtualizedChatRooms.get(uuid);
  virtualizedChatRooms.set(uuid, {
    isViewingLatest: room?.isViewingLatest ?? true,
    mounts: (room?.mounts ?? 0) + 1,
  });

  return () => {
    const current = virtualizedChatRooms.get(uuid);
    if (current && current.mounts > 1) {
      current.mounts -= 1;
    } else {
      virtualizedChatRooms.delete(uuid);
    }
  };
};

export const setVirtualizedChatRoomViewingLatest = (
  uuid: string,
  isViewingLatest: boolean,
) => {
  const room = virtualizedChatRooms.get(uuid);
  if (room) {
    room.isViewingLatest = isViewingLatest;
  }
};

/**
 * Whether the viewer is reading older messages in a virtualized list. New
 * messages must not trim the oldest ones then (the viewer may be scrolling up
 * into them); the store stops taking new messages instead.
 */
export const isViewingOlderChatRoomMessages = (uuid: string) =>
  virtualizedChatRooms.get(uuid)?.isViewingLatest === false;

/**
 * The store trims a room's messages to `kept` once there are more than `max`.
 */
export const getChatRoomMessageLimits = (uuid: string) =>
  virtualizedChatRooms.has(uuid)
    ? { kept: VIRTUALIZED_KEPT_MESSAGES, max: VIRTUALIZED_MAX_MESSAGES }
    : { kept: MESSAGES_THRESHOLD, max: MAX_MESSAGES_THRESHOLD };

export const shouldTrimChatRoomMessages = (
  uuid: string,
  messageCount: number,
) => messageCount > getChatRoomMessageLimits(uuid).max;
